
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import {
  UsersIcon,
  CreditCardIcon,
  DumbbellIcon,
  ActivityIcon,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getDashboardData } from '@/api/dashboard';
import { formatCurrency } from '@/lib/format-currency';
import { cn } from '@/lib/utils';

interface DashboardStatsProps {
  selectedGym: string;
}

function TrendChip({ change }: { change: number }) {
  const positive = change >= 0;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-medium',
        positive
          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
          : 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400'
      )}
    >
      {positive ? (
        <ArrowUpRight className="h-3 w-3" />
      ) : (
        <ArrowDownRight className="h-3 w-3" />
      )}
      {positive ? '+' : ''}
      {change}%
    </span>
  );
}

export function DashboardStats({ selectedGym }: DashboardStatsProps) {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ['dashboardData', selectedGym],
    queryFn: () => getDashboardData(selectedGym),
    enabled: !!selectedGym,
  });

  if (isLoading) {
    return (
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, index) => (
          <Card key={index}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="h-3 w-24 animate-pulse rounded bg-muted" />
                <div className="h-9 w-9 animate-pulse rounded-lg bg-muted" />
              </div>
              <div className="mt-3 h-7 w-20 animate-pulse rounded bg-muted" />
              <div className="mt-2 h-4 w-28 animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Count active check-ins (where checkOut is null)
  const activeCheckIns =
    data?.attandanceCount?.filter((checkin) => checkin.checkOut === null)
      ?.length || 0;

  const stats = [
    {
      title: t('dashboard.totalUsers'),
      value: data?.totalUsers.toString() || '0',
      change: data?.totalUsersChange,
      icon: UsersIcon,
    },
    {
      title: t('dashboard.activeUsers'),
      value: data?.activeUsers.toString() || '0',
      change: data?.activeUsersChange,
      icon: ActivityIcon,
    },
    {
      title: t('dashboard.totalRevenue'),
      value: formatCurrency(data?.totalRevenue || 0, 'ALL'),
      secondary: formatCurrency((data?.totalRevenue ?? 0) / 100, 'EUR'),
      change: data?.totalRevenueChange,
      icon: CreditCardIcon,
    },
    {
      title: t('dashboard.activeCheckIns'),
      value: activeCheckIns.toString(),
      description: t('dashboard.currentlyInGym'),
      icon: DumbbellIcon,
    },
  ];

  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
      {stats.map((stat, index) => (
        <Card key={index} className="transition-shadow hover:shadow-md">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {stat.title}
              </p>
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <stat.icon className="h-4 w-4" />
              </div>
            </div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-2xl font-semibold tracking-tight">
                {stat.value}
              </span>
              {stat.secondary && (
                <span className="text-sm text-muted-foreground">
                  {stat.secondary}
                </span>
              )}
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              {typeof stat.change === 'number' ? (
                <>
                  <TrendChip change={stat.change} />
                  <span>{t('dashboard.fromLastMonth', 'vs last month')}</span>
                </>
              ) : (
                <span>{stat.description}</span>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
